import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import type { SupabaseClient } from '@supabase/supabase-js';

import {
  exportDocumentToGoogleDocs,
  exportSlidesToGoogleSlides,
  GoogleWorkspaceError,
  importGoogleDriveFiles as importGoogleDriveFilesAction,
  importNativeDocs as importNativeDocsAction,
  importNativeSlides as importNativeSlidesAction,
} from './services/googleWorkspaceService';

import type {
  CreateResourceRequest,
  GoogleDriveImportResult,
  GenerateFromTemplateRequest,
  GenerateFromTemplateResult,
  GoogleDocsExportResult,
  Resource,
  ResourceParentLocator,
  ResourceTemplate,
  UpdateResourceRequest,
  GoogleNativeImportResult,
} from '@/shared/types/resource';
import { authHeaders, GENERATE_TEMPLATE_ENDPOINT } from '@/features/chatbot/engine/backend';
import { sbq, throwOnError } from '@/shared/utils/supabaseQuery';
import {
  reorderByPlacement,
  type ReorderAnchorPair,
  type ReorderPlacement,
} from '@/shared/utils/reorder';

type CreateResourceArgs = ResourceParentLocator & {
  resource: CreateResourceRequest;
};

type MoveResourceArgs = ResourceParentLocator & {
  resourceId: string;
  placement: ReorderPlacement;
  anchors: ReorderAnchorPair;
};

type CreateFromTemplateArgs = ResourceParentLocator & {
  templateId: string;
  title?: string;
};

type DeleteResourceArgs = ResourceParentLocator & {
  resourceId: string;
};

type AttachLibraryRubricArgs = ResourceParentLocator & {
  rubricId: string;
};

type ResourceTemplateRow = {
  id: string;
  template_kind: 'document';
  title: string;
  description: string | null;
  base_content: unknown;
  [key: string]: unknown;
};

type ResourceMutationError = {
  status: 'BAD_REQUEST' | 'CUSTOM_ERROR' | 'FETCH_ERROR' | 'NOT_FOUND' | number;
  error: string;
  data?: {
    code?: string;
    error?: string;
    details?: unknown;
  };
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseGenerateFromTemplateResult(value: unknown): GenerateFromTemplateResult {
  if (!isRecord(value) || typeof value.resourceId !== 'string') {
    throw new Error('Template generation returned an invalid response.');
  }

  return {
    resourceId: value.resourceId,
    resourceKind: value.resourceKind === 'slide' ? 'slide' : 'document',
  };
}

function mapResource(row: any): Resource {
  const rawKind = typeof row?.resource_kind === 'string' ? row.resource_kind : 'link';
  const resourceKind =
    rawKind === 'document' ||
    rawKind === 'slide' ||
    rawKind === 'file' ||
    rawKind === 'link' ||
    rawKind === 'rubric'
      ? rawKind
      : 'link';

  return {
    id: row.id ?? '',
    resourceKind,
    title: row.title ?? 'Untitled Resource',
    description: row.description ?? null,
    content: row.content ?? {},
    version: row.version ?? 1,
    ownerId: row.owner_id ?? '',
    createdAt: row.created_at ?? '',
    updatedAt: row.updated_at ?? '',
    archivedAt: row.archived_at ?? null,
    parentCourseId: row.parent_course_id ?? null,
    parentUnitId: row.parent_unit_id ?? null,
    parentLessonId: row.parent_lesson_id ?? null,
    courseId: row.course_id ?? '',
    position: row.position ?? 0,
  } as Resource;
}

function toGoogleWorkspaceMutationError(error: unknown, fallback: string): ResourceMutationError {
  if (error instanceof GoogleWorkspaceError) {
    return {
      status: 'CUSTOM_ERROR',
      error: error.message,
      data: {
        code: error.code,
        error: error.message,
        details: error.details,
      },
    };
  }

  if (error instanceof Error && error.message.trim()) {
    return {
      status: 'FETCH_ERROR',
      error: error.message,
    };
  }

  return {
    status: 'FETCH_ERROR',
    error: fallback,
  };
}

async function getRequiredUserId(sb: SupabaseClient): Promise<string> {
  const { data, error } = await sb.auth.getUser();
  if (error) throw error;
  if (!data?.user?.id) {
    throw new Error('You must be logged in to create or attach resources.');
  }
  return data.user.id;
}

function parentColumns(locator: ResourceParentLocator) {
  const cols: Record<string, unknown> = {
    parent_course_id: locator.parentCourseId ?? null,
    parent_unit_id: locator.parentUnitId ?? null,
    parent_lesson_id: locator.parentLessonId ?? null,
  };
  // course_id is derived server-side by the resource_assign_position trigger
  // when not explicitly provided (e.g. shared editors without course access)
  if (locator.courseId) {
    cols.course_id = locator.courseId;
  }
  return cols;
}

function resourceListTag(locator: ResourceParentLocator) {
  const parentId =
    locator.parentLessonId ?? locator.parentUnitId ?? locator.parentCourseId ?? 'unknown';
  return { type: 'ResourceList' as const, id: parentId };
}

function moveResourceLocator(args: MoveResourceArgs): ResourceParentLocator {
  return {
    parentCourseId: args.parentCourseId,
    parentUnitId: args.parentUnitId,
    parentLessonId: args.parentLessonId,
    courseId: args.courseId,
  };
}

export const resourcesApi = createApi({
  reducerPath: 'resourcesApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Resource', 'ResourceList', 'ResourceTemplate'],
  endpoints: (builder) => ({
    getResourceById: builder.query<Resource, string>({
      queryFn: sbq(async (sb, resourceId) => {
        const result = throwOnError(
          await sb
            .from('resources')
            .select('*')
            .eq('id', resourceId)
            .eq('is_deleted', false)
            .single()
        );
        return mapResource(result);
      }, 'Resource'),
      providesTags: (_result, _error, id) => [{ type: 'Resource', id }],
    }),

    getResources: builder.query<Resource[], ResourceParentLocator>({
      queryFn: sbq(async (sb, locator) => {
        let query = sb.from('resources').select('*').eq('is_deleted', false);

        if (locator.parentLessonId) {
          query = query.eq('parent_lesson_id', locator.parentLessonId);
        } else if (locator.parentUnitId) {
          query = query.eq('parent_unit_id', locator.parentUnitId);
        } else if (locator.parentCourseId) {
          query = query.eq('parent_course_id', locator.parentCourseId);
        }

        const rows = throwOnError(await query.order('position', { ascending: true }));
        return (rows ?? []).map(mapResource);
      }, 'Resource'),
      providesTags: (_result, _error, locator) => [resourceListTag(locator)],
    }),

    createResource: builder.mutation<Resource, CreateResourceArgs>({
      queryFn: sbq(async (sb, { resource, ...locator }) => {
        const userId = await getRequiredUserId(sb);
        const row = throwOnError(
          await sb
            .from('resources')
            .insert({
              owner_id: userId,
              resource_kind: resource.resourceKind,
              title: resource.title,
              description: resource.description ?? null,
              content: resource.content ?? {},
              ...parentColumns(locator),
            })
            .select('*')
            .single()
        );
        return mapResource(row);
      }, 'Resource'),
      invalidatesTags: (_result, _error, args) => [resourceListTag(args)],
    }),

    updateResource: builder.mutation<Resource, UpdateResourceRequest>({
      queryFn: sbq(async (sb, update) => {
        const { id, ...fields } = update;
        if (!id) throw new Error('Resource ID required');

        const payload: Record<string, unknown> = {
          ...fields,
          updated_at: new Date().toISOString(),
        };
        if (fields.version !== undefined) {
          payload.version = fields.version;
        }

        const updated = throwOnError(
          await sb.from('resources').update(payload).eq('id', id).select('*').single()
        );
        return mapResource(updated);
      }, 'Resource'),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Resource', id: args.id },
        'ResourceList',
      ],
    }),

    deleteResource: builder.mutation<void, DeleteResourceArgs>({
      queryFn: sbq(async (sb, { resourceId }) => {
        const { error } = await sb.rpc('soft_delete_resource', { p_resource_id: resourceId });
        if (error) throw error;
        return undefined;
      }, 'Resource'),
      invalidatesTags: (_result, _error, args) => [resourceListTag(args)],
    }),

    moveResource: builder.mutation<void, MoveResourceArgs>({
      queryFn: sbq(async (sb, { resourceId, anchors }) => {
        const { error } = await sb.rpc('move_resource', {
          p_id: resourceId,
          p_before: anchors.beforeId,
          p_after: anchors.afterId,
        });
        if (error) throw error;
        return undefined;
      }, 'Resource'),
      invalidatesTags: (_result, _error, args) => [resourceListTag(args)],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const locator = moveResourceLocator(args);
        const patchResult = dispatch(
          resourcesApi.util.updateQueryData('getResources', locator, (draft) => {
            draft.splice(
              0,
              draft.length,
              ...reorderByPlacement(draft, args.resourceId, args.placement)
            );
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    attachLibraryRubricAsResource: builder.mutation<Resource, AttachLibraryRubricArgs>({
      queryFn: sbq(async (sb, { rubricId, ...locator }) => {
        const userId = await getRequiredUserId(sb);

        const source: any = throwOnError(
          await sb
            .from('rubrics')
            .select('id, title, description, content')
            .eq('id', rubricId)
            .eq('is_deleted', false)
            .single()
        );

        if (!source?.content) throw new Error('Source rubric has no content to copy.');

        const row = throwOnError(
          await sb
            .from('resources')
            .insert({
              owner_id: userId,
              resource_kind: 'rubric',
              title: source.title ?? 'Untitled Rubric',
              description: source.description ?? null,
              content: { rubric: source.content, sourceRubricId: source.id },
              ...parentColumns(locator),
            })
            .select('*')
            .single()
        );
        return mapResource(row);
      }, 'Resource'),
      invalidatesTags: (_result, _error, args) => [resourceListTag(args)],
    }),

    getResourceTemplates: builder.query<ResourceTemplate[], { templateKind?: 'document' }>({
      queryFn: sbq(async (sb, args) => {
        let query = sb
          .from('resource_templates')
          .select('*')
          .eq('is_active', true)
          .order('updated_at', { ascending: false });
        if (args.templateKind) {
          query = query.eq('template_kind', args.templateKind);
        }
        const rows = throwOnError(await query);
        return (rows ?? []).map((row: any) => ({
          id: row.id,
          templateKind: row.template_kind,
          title: row.title,
          description: row.description ?? null,
          categories: row.categories ?? [],
          gradeBands: row.grade_bands ?? [],
          duration: row.duration ?? null,
          difficulty: row.difficulty ?? null,
          tags: row.tags ?? [],
          preview: row.preview ?? null,
          baseContent: row.base_content ?? {},
          meta: row.meta ?? null,
          agentInstructions: row.agent_instructions ?? null,
          generatorProfile: row.generator_profile ?? 'standard',
          isActive: row.is_active ?? true,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        }));
      }, 'ResourceTemplate'),
      providesTags: ['ResourceTemplate'],
    }),

    createFromTemplate: builder.mutation<Resource, CreateFromTemplateArgs>({
      queryFn: sbq(async (sb, { templateId, title, ...locator }) => {
        const userId = await getRequiredUserId(sb);
        const template = throwOnError<ResourceTemplateRow>(
          (await sb
            .from('resource_templates')
            .select('*')
            .eq('id', templateId)
            .eq('is_active', true)
            .single()) as any
        );

        const row = throwOnError(
          await sb
            .from('resources')
            .insert({
              owner_id: userId,
              resource_kind: template.template_kind,
              title: title ?? template.title,
              description: template.description,
              content: template.base_content ?? {},
              ...parentColumns(locator),
            })
            .select('*')
            .single()
        );
        return mapResource(row);
      }, 'Resource'),
      invalidatesTags: (_result, _error, args) => [resourceListTag(args)],
    }),

    generateFromTemplate: builder.mutation<GenerateFromTemplateResult, GenerateFromTemplateRequest>(
      {
        queryFn: sbq(async (sb, args) => {
          const {
            data: { session },
          } = await sb.auth.getSession();
          if (!session?.access_token)
            throw new Error('You must be logged in to generate resources.');

          if (!args.courseId) throw new Error('Course context is required to generate resources.');

          const response = await fetch(GENERATE_TEMPLATE_ENDPOINT, {
            method: 'POST',
            headers: authHeaders(session.access_token),
            body: JSON.stringify({
              templateId: args.templateId,
              parentCourseId: args.parentCourseId ?? null,
              parentUnitId: args.parentUnitId ?? null,
              parentLessonId: args.parentLessonId ?? null,
              courseId: args.courseId,
              userContext: args.userContext,
            }),
          });

          if (!response.ok) {
            const body = await response.json().catch(() => ({ error: 'Generation failed' }));
            throw new Error(body.error ?? `Generation failed (${response.status})`);
          }

          const result = await response.json();
          return parseGenerateFromTemplateResult(result);
        }, 'Resource'),
        invalidatesTags: (_result, _error, args) => {
          const parentId =
            args.parentLessonId ?? args.parentUnitId ?? args.parentCourseId ?? 'unknown';
          return [{ type: 'ResourceList', id: parentId }, 'Resource'];
        },
      }
    ),

    importGoogleDriveFiles: builder.mutation<
      GoogleDriveImportResult,
      ResourceParentLocator & { fileIds: string[] }
    >({
      queryFn: async (args) => {
        try {
          const data = await importGoogleDriveFilesAction(args);
          return { data };
        } catch (error) {
          return {
            error: toGoogleWorkspaceMutationError(error, 'Google Drive import failed.'),
          };
        }
      },
      invalidatesTags: (_result, _error, args) => [resourceListTag(args), 'Resource'],
    }),

    importNativeSlides: builder.mutation<
      GoogleNativeImportResult,
      ResourceParentLocator & { fileIds: string[] }
    >({
      queryFn: async (args) => {
        try {
          const data = await importNativeSlidesAction(args);
          return { data };
        } catch (error) {
          return {
            error: toGoogleWorkspaceMutationError(error, 'Google Slides native import failed.'),
          };
        }
      },
      invalidatesTags: (_result, _error, args) => [resourceListTag(args), 'Resource'],
    }),

    importNativeDocs: builder.mutation<
      GoogleNativeImportResult,
      ResourceParentLocator & { fileIds: string[] }
    >({
      queryFn: async (args) => {
        try {
          const data = await importNativeDocsAction(args);
          return { data };
        } catch (error) {
          return {
            error: toGoogleWorkspaceMutationError(error, 'Google Docs native import failed.'),
          };
        }
      },
      invalidatesTags: (_result, _error, args) => [resourceListTag(args), 'Resource'],
    }),

    exportResourceToGoogle: builder.mutation<
      GoogleDocsExportResult,
      { resourceId: string; resourceKind?: string }
    >({
      queryFn: async ({ resourceId, resourceKind }) => {
        try {
          if (resourceKind === 'slide') {
            const result = await exportSlidesToGoogleSlides(resourceId);
            return { data: { documentId: result.presentationId, url: result.url } };
          }
          const data = await exportDocumentToGoogleDocs(resourceId);
          return { data };
        } catch (error) {
          const fallbackMessage =
            resourceKind === 'slide'
              ? 'Google Slides export failed.'
              : 'Google Docs export failed.';
          return {
            error: toGoogleWorkspaceMutationError(error, fallbackMessage),
          };
        }
      },
    }),
  }),
});

export const {
  useGetResourceByIdQuery,
  useGetResourcesQuery,
  useCreateResourceMutation,
  useUpdateResourceMutation,
  useDeleteResourceMutation,
  useMoveResourceMutation,
  useAttachLibraryRubricAsResourceMutation,
  useGetResourceTemplatesQuery,
  useCreateFromTemplateMutation,
  useGenerateFromTemplateMutation,
  useImportGoogleDriveFilesMutation,
  useImportNativeDocsMutation,
  useImportNativeSlidesMutation,
  useExportResourceToGoogleMutation,
} = resourcesApi;
